/**
 * ServicesPage — catalog of services the user can request.
 *
 * Reached from the Dashboard's "Services" nav card. Routing: AppRouter
 * mounts this under `/services` as a ProtectedRoute (requires a non-null
 * user — the same gating as ProfilePage; you can't browse services
 * before you've finished signup).
 *
 * Data flow:
 *   - One `useQuery(['services'])` fetches the FULL list once.
 *   - Category tabs are derived from the unique `category` values in
 *     the response — that way we don't hardcode a category enum, and
 *     when the backend adds a new category, a tab appears automatically.
 *   - Filtering by tab is CLIENT-SIDE. The backend supports
 *     `?category=` server-side filtering, but with the expected catalog
 *     size (<= 50 services) one fetch + slice is faster UX (no spinner
 *     on tab switch) and simpler code. If the catalog grows past a few
 *     hundred we'd switch to server-side and re-fetch per tab.
 *
 * URL state:
 *   The active category lives in `?category=<code>` so it survives
 *   navigating to a service detail and back. "All" is represented by
 *   the absence of the param, not `category=all` — cleaner URLs.
 *
 * Tap target:
 *   Whole card is a <CardActionArea> that navigates to /services/:id.
 *   No separate "View" button.
 *
 * Detail-page cache reuse:
 *   `useQuery(['services'])` from this page populates the cache.
 *   ServiceDetailPage looks up its service from THE SAME cache key,
 *   so visiting list → detail is instant (no extra fetch). If the user
 *   lands on the detail page via direct URL, that page's identical
 *   useQuery call triggers the fetch.
 */

import { useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Box,
  IconButton,
  Tabs,
  Tab,
  Skeleton,
  Alert,
  Button,
  Badge,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

import LanguageToggle from '@/components/LanguageToggle';
import CurrentClinicBanner from '@/components/CurrentClinicBanner';
import CartBar from '@/components/CartBar';
import { servicesApi } from '@/services/api';
import { selectLanguage, selectIsRtl } from '@/store/uiSlice';
import { selectCurrentClinic } from '@/store/clinicSlice';
import { selectCartCount } from '@/store/cartSlice';
import { formatToman } from '@/utils/currency';
import { localized } from '@/utils/localizedContent';

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * Translate a category code (e.g. "face_analysis") to its localized
 * label. Falls back to the raw code if we don't have a translation —
 * that way new backend categories don't render as blank text.
 *
 * @param {string} code
 * @param {(key: string) => string} t
 */
function getCategoryLabel(code, t) {
  const key = `services.category.${code}`;
  const translated = t(key);
  return translated === key ? code : translated;
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

/**
 * One card in the list. Tappable; navigates to the detail page.
 */
function ServiceCard({ service, t, language, onOpen }) {
  // Pre-resolve bilingual fields so the description-gate works correctly
  // (an empty {fa:'',en:''} object is still truthy — must check the
  // localized string).
  const name = localized(service.name, language);
  const description = localized(service.description, language);
  return (
    <Card variant="outlined">
      <CardActionArea onClick={() => onOpen(service.id)}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {name}
              </Typography>
              {description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    // Single-line preview with ellipsis. Multi-line clamp
                    // works cross-browser via -webkit-line-clamp.
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mb: 1,
                  }}
                >
                  {description}
                </Typography>
              )}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  size="small"
                  label={getCategoryLabel(service.category, t)}
                  variant="outlined"
                />
                {service.durationMinutes != null && (
                  <Typography variant="caption" color="text.secondary">
                    {t('services.card.durationMinutes', {
                      count: service.durationMinutes,
                    })}
                  </Typography>
                )}
              </Stack>
            </Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="primary.main"
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {formatToman(service.priceToman, language)}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/**
 * Loading skeleton — a stack of placeholder cards that roughly matches
 * the final ServiceCard layout, so the page doesn't visually jump when
 * data arrives.
 */
function SkeletonList() {
  return (
    <Stack spacing={2}>
      {[1, 2, 3].map((i) => (
        <Card key={i} variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={28} />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="text" width="40%" />
              </Box>
              <Skeleton variant="text" width={90} height={28} />
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

/**
 * Empty state for "no services in this category". Different copy
 * depending on whether a filter is active.
 */
function EmptyState({ activeCategory, t }) {
  const messageKey =
    activeCategory === 'all'
      ? 'services.empty.allMessage'
      : 'services.empty.categoryMessage';
  return (
    <Stack
      alignItems="center"
      spacing={2}
      sx={{ py: 6, color: 'text.secondary' }}
    >
      <SearchOffIcon sx={{ fontSize: 48, opacity: 0.5 }} />
      <Typography variant="body1" textAlign="center">
        {t(messageKey)}
      </Typography>
    </Stack>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                          */
/* ------------------------------------------------------------------ */

export function ServicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const language = useSelector(selectLanguage);
  const isRtl = useSelector(selectIsRtl);
  const currentClinic = useSelector(selectCurrentClinic);
  const cartCount = useSelector(selectCartCount);
  const [searchParams, setSearchParams] = useSearchParams();

  // When the user is "in" a clinic, the catalog is scoped to the services
  // that clinic offers. The clinic code is part of the query key so each
  // clinic gets its own cache entry (and switching clinic refetches).
  // No clinic → the unscoped catalog (browse mode).
  const clinicCode = currentClinic?.code;
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['services', clinicCode ?? 'all'],
    queryFn: () =>
      servicesApi.listServices({ pageSize: 50, ...(clinicCode ? { clinicCode } : {}) }),
    // Services don't change often. 5 minutes is a sensible default;
    // we can tune once we see real usage.
    staleTime: 5 * 60 * 1000,
  });

  const allServices = useMemo(() => data?.data?.services ?? [], [data]);

  // Distinct categories sorted alphabetically. Memoized so the Tabs
  // value doesn't churn on every render.
  const categories = useMemo(
    () =>
      Array.from(new Set(allServices.map((s) => s.category))).sort(),
    [allServices]
  );

  // Active category lives in the URL. "all" = no param.
  const activeCategory = searchParams.get('category') ?? 'all';

  const setActiveCategory = useCallback(
    (cat) => {
      // Use functional updater to avoid stale closure on searchParams.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (cat === 'all') next.delete('category');
          else next.set('category', cat);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // If the URL says category=foo but foo isn't a valid category in the
  // fetched data (e.g., stale bookmark), the Tabs would error on the
  // mismatch. Coerce back to 'all' for display.
  const tabValue = categories.includes(activeCategory) ? activeCategory : 'all';

  const filtered = useMemo(() => {
    if (tabValue === 'all') return allServices;
    return allServices.filter((s) => s.category === tabValue);
  }, [allServices, tabValue]);

  const handleOpen = (serviceId) => {
    navigate(`/services/${serviceId}`);
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={() => navigate('/dashboard')}
              aria-label={t('common.back')}
            >
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <MedicalServicesIcon color="primary" />
            <Typography variant="h5" component="h1" fontWeight={700}>
              {t('services.title')}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton
              onClick={() => navigate('/cart')}
              aria-label={t('cart.title')}
            >
              <Badge badgeContent={cartCount} color="primary">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>
            <LanguageToggle />
          </Stack>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {t('services.subtitle')}
        </Typography>

        {/* Current-clinic banner — services below are scoped to this clinic */}
        <CurrentClinicBanner />

        {/* Tabs — hidden during initial load to avoid a flash of an
            empty bar before any categories are known */}
        {!isLoading && !isError && categories.length > 0 && (
          <Tabs
            value={tabValue}
            onChange={(_e, v) => setActiveCategory(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              minHeight: 40,
              '& .MuiTab-root': { minHeight: 40 },
            }}
          >
            <Tab value="all" label={t('services.tabs.all')} />
            {categories.map((cat) => (
              <Tab
                key={cat}
                value={cat}
                label={getCategoryLabel(cat, t)}
              />
            ))}
          </Tabs>
        )}

        {/* Body */}
        {isLoading && <SkeletonList />}

        {isError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {t('services.error.loadFailed')}
          </Alert>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <EmptyState activeCategory={tabValue} t={t} />
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <Stack spacing={2}>
            {filtered.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                t={t}
                language={language}
                onOpen={handleOpen}
              />
            ))}
          </Stack>
        )}
      </Stack>
      <CartBar />
    </Container>
  );
}

export default ServicesPage;
